import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import SharedNavigation from "@/components/SharedNavigation";
import PresenceDot from "@/components/PresenceDot";

const RGM_LOGO_FALLBACK = "/rgm-logo.png";

const SectionRow = ({ item, idx }) => {
  const hasRank = item.rank != null && item.rank > 0;
  // Phase 15.6L — cascade image fallback so we never show an empty box.
  // Track Cover → Artist Photo (fallback_cover) → RGM Logo.
  const [imgSrc, setImgSrc] = useState(item.cover_art || item.fallback_cover || RGM_LOGO_FALLBACK);
  const [errCount, setErrCount] = useState(0);
  const onErr = () => {
    if (errCount === 0 && item.fallback_cover && imgSrc !== item.fallback_cover) {
      setImgSrc(item.fallback_cover);
      setErrCount(1);
    } else if (imgSrc !== RGM_LOGO_FALLBACK) {
      setImgSrc(RGM_LOGO_FALLBACK);
      setErrCount(2);
    }
  };
  const usingLogoFallback = imgSrc === RGM_LOGO_FALLBACK;
  return (
    <Link
      to={item.link || "#"}
      className="group flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/[0.03] hover:bg-white/[0.08] border border-white/5 transition"
      data-testid={`winners-row-${item.submission_id || item.user_id || item.crew_id || item.beat_id || idx}`}
    >
      <div className="w-6 text-center font-black text-purple-300 text-sm flex-shrink-0">
        {hasRank ? item.rank === 1 ? "🥇" : item.rank === 2 ? "🥈" : item.rank === 3 ? "🥉" : `#${item.rank}` : ""}
      </div>
      <div className={`w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-white/10 ${usingLogoFallback ? "bg-gradient-to-br from-purple-900/40 to-pink-900/40 p-1.5" : "bg-gray-800"}`} data-testid={`winners-cover-${item.submission_id || item.user_id || item.crew_id || item.beat_id || idx}`}>
        <img
          src={imgSrc}
          alt=""
          className={`w-full h-full ${usingLogoFallback ? "object-contain opacity-80" : "object-cover"}`}
          onError={onErr}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-semibold text-sm truncate group-hover:text-purple-300 flex items-center gap-1.5">
          <span className="truncate">{item.title}</span>
          {item.user_id && !item.submission_id && !item.crew_id && !item.beat_id && (
            <PresenceDot userId={item.user_id} size="sm" />
          )}
        </div>
        {item.artist_name && item.artist_name !== item.title && (
          <div className="text-[11px] text-gray-400 truncate">{item.artist_name}</div>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-amber-300 font-bold text-sm">
          {(item.stat_value || 0).toLocaleString()}
        </div>
        <div className="text-[10px] text-gray-500">{item.stat_label}</div>
      </div>
    </Link>
  );
};

const WinnersNetworkPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Phase 15.6P fix: hydrate currentUser so SharedNavigation renders the
        // authed identity (wallet chip, Creator Hub button, mobile drawer)
        // instead of degrading to the anonymous "Artist Portal" UI.
        const [winners, me] = await Promise.allSettled([
          api.get("/winners-network", { params: { lookback_days: 7, per_section: 6 } }),
          api.get("/auth/me"),
        ]);
        if (winners.status === "fulfilled") setData(winners.value.data);
        else setError(winners.reason?.response?.data?.detail || "Failed to load winners");
        if (me.status === "fulfilled") setCurrentUser(me.value.data?.user || me.value.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-black" data-testid="winners-network-page">
      <SharedNavigation currentUser={currentUser} activePage="winners" />
      <div className="pt-20 px-4 pb-12 max-w-7xl mx-auto">
        <header className="mb-6">
          <h1 className="text-4xl sm:text-5xl font-black text-white">🏆 Weekly Winners Network</h1>
          <p className="text-gray-400 text-sm mt-2 max-w-2xl">
            Spotlight winners, top artists, crews, beats and the latest verified artists — refreshed daily.
          </p>
        </header>

        {loading && <div className="text-gray-400">Loading…</div>}
        {error && <div className="text-rose-300" data-testid="winners-error">{error}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="winners-sections">
          {data?.sections?.map((section) => (
            <Card
              key={section.key}
              className="p-4 bg-gradient-to-br from-purple-900/20 to-pink-900/10 border-purple-500/30"
              data-testid={`winners-section-${section.key}`}
            >
              <div className="mb-3">
                <h2 className="text-white font-bold text-lg">{section.title}</h2>
                <p className="text-[11px] text-gray-400 mt-0.5">{section.subtitle}</p>
              </div>
              {section.items.length === 0 ? (
                <p className="text-gray-500 text-sm italic" data-testid={`winners-empty-${section.key}`}>
                  Nothing here yet — check back this week.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {section.items.map((item, idx) => (
                    <li key={`${section.key}-${idx}`}>
                      <SectionRow item={item} idx={idx} />
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WinnersNetworkPage;
